import { find as _find, get as _get } from 'lodash-es';
import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { useQuery } from '@apollo/client/react';
import RadioGroupField from './radio-group-field';
import { useAppContext } from '../../contexts/app';
import { myGroupsQuery } from '../../apollo/groups';
import HostGroup from '../host-group';
import Host from '../host';
import type { User } from '../../types/User';
import type { Group } from '../../types/Group';

type Props = {
  name: string;
  disabled?: boolean;
  user?: User;
  group?: Group;
  [key: string]: unknown;
};

const HostField = ({ name, disabled, user: userOverride, group: groupOverride, ...rest }: Props) => {
  const { loggedInUser, isLoggedIn } = useAppContext();
  const {
    values: { groupId },
  } = useFormikContext<{ groupId: string }>();
  const {
    data: groupsData,
    loading: groupLoading,
  } = useQuery(myGroupsQuery, {
    skip: !isLoggedIn,
  });

  const group = useMemo(() => {
    if (!groupId) {
      return null;
    }
    if (groupOverride && groupId === groupOverride.id) {
      return groupOverride;
    }
    if (!groupsData) {
      return null;
    }
    return _find(_get(groupsData, 'me.groups', []), (g) => g.id === groupId);
  }, [groupsData, groupId, groupOverride]);

  const user = userOverride || loggedInUser;

  const options = useMemo(() => {
    return [
      {
        value: 'User',
        label: user ?
          <Host
            event={{
              hostType: 'User',
              user: user,
              group,
            }}
            simple
            target="_blank"
          /> :
          'You',
      },
      ...(group && group.permissions.editable ? [{
        value: 'Group',
        label: (
          <HostGroup
            group={group}
            simple
            target="_blank"
          />
        ),
      }] : []),
    ];
  }, [user, group]);

  return (
    <RadioGroupField
      name={name}
      {...rest}
      disabled={disabled || groupLoading}
      options={options}
    />
  );
};

export default HostField;
